<template>
  <v-dialog
    :value="value"
    fullscreen
    persistent
    @input="handleInput"
  >
    <v-card
      tile
      class="d-flex flex-column wizard-card"
    >
      <!-- ステッパーは wizard-body の外に出して、step の position: absolute と重ならないようにする -->
      <wizard-stepper
        class="wizard-stepper-top"
        :current-step="currentStep"
        :step-names="stepNames"
        :next-disabled="nextDisabled"
        @navigate="goToStep"
      />

      <v-card-text class="wizard-body flex-grow-1 pa-0">
        <!-- Step: 席種・客数・税サ・開始時間 -->
        <init-config-course-step
          v-if="currentStepKey === 'init'"
          :courses="courses"
          :selected-course-id="formState.selectedCourseId"
          :customer-count="formState.customerCount"
          :tax-charge="formState.taxCharge"
          :start-at="formState.startAt"
          @update:selectedCourseId="formState.selectedCourseId = $event"
          @update:customerCount="formState.customerCount = $event"
          @update:taxCharge="handleTaxChargeChange"
          @update:startAt="formState.startAt = $event"
        />

        <!-- Step: お客様情報 (指名/リファラル主軸 + 顧客は卓全体に紐付け) -->
        <customer-info-step
          v-else-if="currentStepKey === 'customer'"
          :nominations="nominations"
          :users="users"
          :referrals="referrals"
          :selected-customer-ids="formState.selectedCustomerIds"
          :cast-nominations="formState.castNominations"
          :referral-rows-value="formState.referralRows"
          :customer-table-taggings="formState.customerTableTaggings"
          @update:selectedCustomerIds="formState.selectedCustomerIds = $event"
          @update:castNominations="handleCastNominationsChange"
          @update:referralRowsValue="formState.referralRows = $event"
          @update:customerTableTaggings="formState.customerTableTaggings = $event"
        />

        <!-- Step: チャージ (items が無い場合はスキップ) -->
        <charge-step
          v-else-if="currentStepKey === 'charge'"
          :items="items"
          :charges="formState.charges"
          @update:charges="handleChargesChange"
        />

        <!-- Step: セット時間/料金確認 -->
        <set-confirm-step
          v-else-if="currentStepKey === 'confirm'"
          :customer-count="formState.customerCount"
          :selected-course="selectedCourse"
          :set="formState.set"
          :tax-charge="formState.taxCharge"
          :start-at="formState.startAt"
          :nominations="confirmNominations"
          :charges="confirmCharges"
          :nominations-total="nominationsTotal"
          :charges-total="chargesTotal"
          :checkout-config="checkoutConfig"
          :customer-tag-names="confirmCustomerTagNames"
          :single-charge-setting="matchedChargeSetting"
          :single-charge="formState.singleCharge"
          :deposit-enabled="depositEnabled"
          :deposits="formState.deposits"
          @update:set="formState.set = $event"
          @update:deposits="formState.deposits = $event"
          @update:taxCharge="handleTaxChargeChange"
          @update:startAt="formState.startAt = $event"
          @update:nomination-tax="handleNominationTaxChange"
          @update:charge-tax="handleChargeTaxChange"
          @update:single-charge="formState.singleCharge = $event"
        />
      </v-card-text>

      <wizard-footer
        class="wizard-footer-fixed"
        :current-step="currentStep"
        :total-steps="totalSteps"
        :next-disabled="nextDisabled"
        :save-draft-disabled="saveDraftDisabled"
        :submitting="isSubmitting"
        @prev="goPrev"
        @next="goNext"
        @complete="handleComplete"
        @save-draft="handleSaveDraft"
        @cancel="handleCancel"
      />
    </v-card>

    <!-- 予約重複確認ダイアログ(409 reservation_conflict) -->
    <reservation-conflict-dialog
      v-model="showConflictDialog"
      :conflicts="reservationConflicts"
      :format-conflict="formatConflict"
      @confirm="confirmProceed"
      @cancel="cancelProceed"
    />
  </v-dialog>
</template>

<script>
import {
  ref, reactive, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import TableApi from '@/api/waiter/Table'
import ChargeSettingApi from '@/api/waiter/ChargeSetting'
import { buildTableCreatePayload } from '@/views/composable/useTableCreatePayload'
import useCurrentData from '@/views/composable/useCurrentData'
import useReservationConflictCheck from '@/views/composable/useReservationConflictCheck'
import ReservationConflictDialog from '../ReservationConflictDialog.vue'
import WizardStepper from './WizardStepper.vue'
import WizardFooter from './WizardFooter.vue'
import InitConfigCourseStep from './steps/InitConfigCourseStep.vue'
import CustomerInfoStep from './steps/CustomerInfoStep.vue'
import ChargeStep from './steps/ChargeStep.vue'
import SetConfirmStep from './steps/SetConfirmStep.vue'

export default {
  name: 'TableCreateWizard',
  components: {
    WizardStepper,
    WizardFooter,
    InitConfigCourseStep,
    CustomerInfoStep,
    ChargeStep,
    SetConfirmStep,
    ReservationConflictDialog,
  },
  props: {
    value: { type: Boolean, default: false },
    hallId: { type: [Number, String], required: true },
    tableNumber: { type: [Number, String], required: true },
    tablePrefix: { type: String, default: '' },
    tableSuffix: { type: String, default: '卓' },
    customName: { type: String, default: '' },
    courses: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    nominations: { type: Array, default: () => [] },
    referrals: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    initialCustomerIds: { type: Array, default: () => [] },
    // ホールの会計設定（税・サービス料・丸めなど）
    checkoutConfig: { type: Object, default: () => ({}) },
    // 売上計上トリガ（指名/リファラルから自動的に vending_contributor を決める）
    vendingContributionTriggers: { type: Array, default: () => [] },
  },
  emits: ['input', 'close', 'created'],
  setup(props, { emit }) {
    const { currentClub } = useCurrentData()
    const currentStep = ref(1)
    const hasCharges = computed(() => Array.isArray(props.items) && props.items.length > 0)

    // 現在時刻を 5 分単位で切り上げ (例: 11:56 → 12:00)
    const ceilToFiveMinutes = (date = new Date()) => {
      const d = new Date(date.getTime())
      const m = d.getMinutes()
      d.setMinutes(Math.ceil(m / 5) * 5, 0, 0)

      return d
    }

    const formState = reactive({
      // Step 1
      selectedCourseId: null,
      customerCount: 0,
      taxCharge: 'normal', // 'normal' | 'cut' - Step1 で初期選択、Step4 で上書き可
      // Step 2 (指名/リファラル主軸 + 顧客は卓全体に紐付け)
      selectedCustomerIds: [],
      castNominations: [], // [{ castId, vendibleId, sellingPrice, taxCharge }]
      referralRows: [], // [{ referralId, userId }]
      customerTableTaggings: [], // [{ id, customerTagId, tableId, _destroy, attributes }]
      // Step 3
      charges: [],
      // Step 4: セット開始時間 (Date)
      startAt: ceilToFiveMinutes(),
      // Step 4
      set: {
        mode: 'bulk',
        durationMin: 0,
        price: 0,
        perCustomer: [],
      },
      // シングルチャージの上書き状態(条件マッチ時に設定値で初期化される)
      singleCharge: { rate: 0, applied: true },
      // Step 4: お預かり金 [{ amount, paymentMethod }](初回注文時に預かるケース。権限のある店舗のみ)
      deposits: [],
    })

    // シングルチャージ設定(店舗単位)。人数が条件に一致した場合のみ確認ステップに表示する
    const chargeSettings = ref([])
    const loadChargeSettings = async () => {
      try {
        const res = await ChargeSettingApi.getChargeSettings()
        chargeSettings.value = (res?.data?.data || []).map(d => ({ ...d.attributes }))
      } catch (_) {
        chargeSettings.value = []
      }
    }
    watch(() => props.value, opened => {
      if (opened) loadChargeSettings()
    }, { immediate: true })

    const matchedChargeSetting = computed(() => (
      chargeSettings.value.find(s => Number(s.customerCount) === Number(formState.customerCount)) || null
    ))

    // マッチする設定が変わったら上書き状態を設定値でリセットする
    watch(matchedChargeSetting, setting => {
      formState.singleCharge = {
        rate: setting ? Number(setting.rate) || 0 : 0,
        applied: true,
      }
    })

    // チャージ商品の無い店舗では charge ステップをスキップする
    const stepKeys = computed(() => {
      const keys = ['init', 'customer']
      if (hasCharges.value) keys.push('charge')
      keys.push('confirm')

      return keys
    })
    const stepNames = computed(() => stepKeys.value.map(k => ({
      init: '席種・客数',
      customer: 'お客様情報',
      charge: 'チャージ',
      confirm: 'セット確認',
    }[k])))
    const totalSteps = computed(() => stepKeys.value.length)
    const currentStepKey = computed(() => stepKeys.value[currentStep.value - 1])

    const handleTaxChargeChange = v => {
      formState.taxCharge = v || 'normal'
      // 個別モード時は perCustomer 各行の taxCharge も追従
      if (formState.set.mode === 'individual' && Array.isArray(formState.set.perCustomer)) {
        formState.set.perCustomer = formState.set.perCustomer.map(r => ({
          ...r,
          taxCharge: formState.taxCharge,
        }))
      }
    }

    // Step 1 で選択された Course オブジェクト
    const selectedCourse = computed(() => props.courses
      .find(c => c.id === formState.selectedCourseId) || null)

    // 合計料金内訳
    const nominationsTotal = computed(() => (formState.castNominations || [])
      .reduce((sum, n) => sum + Number(n.sellingPrice || 0), 0))
    const chargesTotal = computed(() => (formState.charges || [])
      .filter(c => c.enabled === true)
      .reduce((sum, c) => sum + Number(c.sellingPrice || 0), 0))

    // Step4 で個別税サを切り替えるための一覧（キャスト名・指名名を解決）
    const castMap = computed(() => {
      const m = new Map();
      (props.users || []).forEach(u => {
        const flat = u.attributes ? { id: u.id, ...u.attributes } : u
        m.set(flat.id, flat)
      })

      return m
    })
    const nominationDefMap = computed(() => {
      const m = new Map();
      (props.nominations || []).forEach(n => {
        const flat = n.attributes ? { id: n.id, ...n.attributes } : n
        m.set(flat.id, flat)
      })

      return m
    })
    const itemMap = computed(() => {
      const m = new Map();
      (props.items || []).forEach(it => {
        const flat = it.attributes ? { id: it.id, ...it.attributes } : it
        m.set(flat.id, flat)
      })

      return m
    })

    // 商品マスタの defaultTaxCharge を優先し、フォールバックとして Club の設定を使用
    // （旧 TableCreateDrawer の addVendingHistoryTemp と同じ優先順位）
    const resolveDefaultTaxCharge = vendible => {
      const vendibleDefault = vendible?.defaultTaxCharge
      if (vendibleDefault && vendibleDefault !== 'normal') return vendibleDefault
      if (currentClub.value?.defaultTaxCharge === 'cut') return 'cut'

      return 'normal'
    }

    const confirmNominations = computed(() => (formState.castNominations || []).map(n => ({
      castId: n.castId,
      vendibleId: n.vendibleId,
      sellingPrice: n.sellingPrice,
      taxCharge: n.taxCharge || 'normal',
      freeBack: !!n.freeBack,
      castName: castMap.value.get(n.castId)?.name || `cast#${n.castId}`,
      vendibleName: nominationDefMap.value.get(n.vendibleId)?.name || '指名',
    })))

    // Step4 サマリー用のタグ名一覧（読み取り専用表示）
    const confirmCustomerTagNames = computed(() => (formState.customerTableTaggings || [])
      .filter(t => !t._destroy)
      .map(t => t.attributes?.name || `タグ#${t.customerTagId}`))

    const confirmCharges = computed(() => (formState.charges || [])
      .filter(c => c.enabled === true)
      .map(c => ({
        vendibleId: c.vendibleId,
        sellingPrice: c.sellingPrice,
        taxCharge: c.taxCharge || 'normal',
        name: itemMap.value.get(c.vendibleId)?.name || `item#${c.vendibleId}`,
      })))

    // Step2 で指名行が追加されたとき、指名マスタの税サ既定値を引き継ぐ
    // （Step4 で個別変更済みの行は taxCharge を保持しているためそのまま）
    const handleCastNominationsChange = rows => {
      formState.castNominations = (rows || []).map(n => (
        n.taxCharge
          ? n
          : { ...n, taxCharge: resolveDefaultTaxCharge(nominationDefMap.value.get(n.vendibleId)) }
      ))
    }

    // Step3 でチャージ行が追加されたとき、アイテムマスタの税サ既定値を引き継ぐ
    const handleChargesChange = rows => {
      formState.charges = (rows || []).map(c => (
        c.taxCharge
          ? c
          : { ...c, taxCharge: resolveDefaultTaxCharge(itemMap.value.get(c.vendibleId)) }
      ))
    }

    const handleNominationTaxChange = ({ castId, vendibleId, taxCharge }) => {
      formState.castNominations = (formState.castNominations || []).map(n => (
        n.castId === castId && n.vendibleId === vendibleId
          ? { ...n, taxCharge: taxCharge || 'normal' }
          : n
      ))
    }

    const handleChargeTaxChange = ({ vendibleId, taxCharge }) => {
      formState.charges = (formState.charges || []).map(c => (
        c.vendibleId === vendibleId
          ? { ...c, taxCharge: taxCharge || 'normal' }
          : c
      ))
    }

    // 選択 Course が変わったら set のデフォルト値を更新
    const courseDurationMin = c => {
      if (!c) return 0
      if (c.effectiveDurationSeconds != null) return Math.round(c.effectiveDurationSeconds / 60)
      if (c.servingTimeMin != null) return c.servingTimeMin
      if (c.durationMin != null) return c.durationMin

      return 0
    }
    watch(selectedCourse, c => {
      if (c) {
        formState.set = {
          ...formState.set,
          durationMin: courseDurationMin(c),
          price: c.sellingPrice || 0,
        }
        // コースのデフォルト税サ設定 (defaultTaxCharge) を Step1 の初期値に反映
        // handleTaxChargeChange 経由で個別モード時の perCustomer 各行にも追従させる
        handleTaxChargeChange(resolveDefaultTaxCharge(c))
      }
    })

    // 後段の payload 組立で使う「customers」を派生
    // 形式: [{ customerId, nominations: [], referrals: [] }, ...]
    // 旧 formState.customers の役割は selectedCustomerIds + castNominations + referralRows に分散
    const derivedCustomers = computed(() => {
      const ids = formState.selectedCustomerIds || []
      if (ids.length === 0) {
        // 顧客未選択: 卓全体として 1 件の匿名 customer を作る
        return [{
          customerId: null,
          nominations: formState.castNominations || [],
          referrals: formState.referralRows || [],
        }]
      }

      // 顧客が選択されている場合: 1 件目に全紐付けを集約（指名/リファラルは卓全体のため）
      return ids.map((id, idx) => ({
        customerId: id,
        nominations: idx === 0 ? (formState.castNominations || []) : [],
        referrals: idx === 0 ? (formState.referralRows || []) : [],
      }))
    })

    // 各ステップの「次へ」活性条件（Phase 3〜6 で詳細化）
    const nextDisabled = computed(() => {
      switch (currentStep.value) {
        case 1:
          return !formState.selectedCourseId || formState.customerCount < 1
        default:
          return false
      }
    })

    // 「一旦作成」は最低限コースと客数があるときだけ有効
    const saveDraftDisabled = computed(() => (
      !formState.selectedCourseId || formState.customerCount < 1
    ))

    const goNext = () => {
      if (currentStep.value < totalSteps.value) {
        currentStep.value += 1
      }
    }

    const goPrev = () => {
      if (currentStep.value > 1) {
        currentStep.value -= 1
      }
    }

    // stepper クリックで任意のステップにジャンプ
    // 通過済みステップは常に / 先のステップは現在ステップが valid な時だけ
    const goToStep = step => {
      if (step < 1 || step > totalSteps.value) return
      if (step <= currentStep.value) {
        currentStep.value = step

        return
      }
      if (!nextDisabled.value) {
        currentStep.value = step
      }
    }

    const isSubmitting = ref(false)
    const vm = getCurrentInstance()?.proxy

    // お預かり金は waiter:deposits 権限のある店舗だけ(卓ドロワーの出し分けと同じ判定)
    const depositEnabled = computed(() => {
      try {
        return !!vm?.$can?.('manage', 'waiter:deposits')
      } catch (_) {
        return false
      }
    })

    // 予約が入っている卓(409)は確認ダイアログを出し、「作成する」で confirm 付きで再送する
    const {
      conflicts: reservationConflicts,
      showConflictDialog,
      confirmProceed,
      cancelProceed,
      executeWithConflictCheck,
      formatConflict,
    } = useReservationConflictCheck()

    // 卓作成 API 呼び出し
    const submitCreateTable = async () => {
      if (isSubmitting.value) return
      isSubmitting.value = true
      try {
        const payload = buildTableCreatePayload({
          startAt: formState.startAt,
          hallId: props.hallId,
          tableNumber: props.tableNumber,
          formState: {
            ...formState,
            customers: derivedCustomers.value,
          },
          selectedCourse: selectedCourse.value,
          vendingContributionTriggers: props.vendingContributionTriggers,
          // 条件マッチ時のみ上書き内容を明示送信(不適用・率変更をサーバに伝える)
          tableCharge: matchedChargeSetting.value
            ? {
              chargeSettingId: matchedChargeSetting.value.id,
              rate: Number(formState.singleCharge?.rate) || 0,
              applied: formState.singleCharge?.applied !== false,
            }
            : null,
          deposits: depositEnabled.value ? formState.deposits : [],
        })
        const res = await executeWithConflictCheck(
          () => TableApi.createTable(payload),
          () => TableApi.createTable({ ...payload, confirmReservationConflict: true }),
        )
        if (!res) {
          // ユーザーが「やめる」を選んだ。ウィザードは閉じずに再試行できるようにする
          return
        }
        const table = res?.data?.data || null
        emit('created', table)
        if (vm?.$toast?.success) {
          vm.$toast.success(`${props.customName || [props.tablePrefix, props.tableNumber, props.tableSuffix].join('')}を作成しました`)
        }
        emit('input', false)
        emit('close')
      } catch (e) {
        if (vm?.$toast?.error) {
          vm.$toast.error('卓の作成に失敗しました')
        }
        // エラー時はウィザードを閉じない（再試行可）
      } finally {
        isSubmitting.value = false
      }
    }

    const handleComplete = async () => {
      await submitCreateTable()
    }

    // 「一旦作成」: 現時点の入力でそのまま卓を作成しウィザードを閉じる
    const handleSaveDraft = async () => {
      await submitCreateTable()
    }

    const handleCancel = () => {
      // TODO: 入力済みなら確認ダイアログ
      emit('input', false)
      emit('close')
    }

    const handleInput = v => {
      emit('input', v)
    }

    return {
      depositEnabled,
      currentStep,
      currentStepKey,
      totalSteps,
      stepNames,
      formState,
      matchedChargeSetting,
      selectedCourse,
      nominationsTotal,
      chargesTotal,
      confirmNominations,
      confirmCharges,
      confirmCustomerTagNames,
      derivedCustomers,
      isSubmitting,
      nextDisabled,
      saveDraftDisabled,
      goNext,
      goPrev,
      goToStep,
      submitCreateTable,
      handleComplete,
      handleSaveDraft,
      handleCancel,
      handleInput,
      handleTaxChargeChange,
      handleCastNominationsChange,
      handleChargesChange,
      handleNominationTaxChange,
      handleChargeTaxChange,
      reservationConflicts,
      showConflictDialog,
      confirmProceed,
      cancelProceed,
      formatConflict,
    }
  },
}
</script>

<style scoped>
/* Wizard 全体: viewport 高さに固定し、stepper と footer を画面端に固定、本文だけスクロール */
.wizard-card {
  /* iOS Safari の URL バー等を考慮: dvh 対応ブラウザでは可視高さ基準にする */
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
}
.wizard-body {
  overflow-y: auto;
  /* 子の絶対配置 step (例: SetConfirmStep) を内側に閉じ込めるための基準 */
  position: relative;
}
.wizard-stepper-top {
  flex: 0 0 auto;
  z-index: 4;
  background: var(--v-background-base);
}
/* Vuetify の v-stepper が持つ既定の影を消す（global なので :deep 不要） */
.wizard-stepper-top .v-stepper,
.wizard-stepper-top .v-stepper__header,
.wizard-stepper .v-stepper__header,
.wizard-stepper.v-stepper {
  box-shadow: none !important;
}
.wizard-footer-fixed {
  position: sticky;
  bottom: 0;
  z-index: 5;
}
</style>

<!-- グローバル: 全ステップ共通レイアウト & セクションカード -->
<style>
.wizard-step {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 16px;
}
.wizard-step-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.wizard-section-card {
  /* 背景色は付けず、枠線のみで構造を示す（テーマ非依存） */
  background: transparent;
  border: 1px solid rgba(127, 127, 127, 0.32);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* デフォルトは自然な高さを保ち、親の wizard-step-body がスクロールする */
  flex: 0 0 auto;
}
.wizard-section-card--fill {
  /* 残り高さを埋めてカード内部スクロール用 */
  flex: 1 1 auto;
  min-height: 200px;
}
.wizard-section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(127, 127, 127, 0.24);
  background: transparent;
  flex: 0 0 auto;
}
.wizard-section-header--accent {
  background: transparent;
}
.wizard-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.wizard-section-body {
  padding: 16px;
}
.wizard-section-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

/* ダークモードで text--secondary (Vuetify デフォルト ~0.6 alpha) が読みづらいため引き上げ */
.theme--dark .wizard-step .text--secondary,
.theme--dark .wizard-step .text-caption.text--secondary {
  color: rgba(255, 255, 255, 0.78) !important;
}

/* ダークモードでは指定ボタンの primary 紫が背景に埋もれるため、
   テーマ追従の中性色（明るい白系）に切り替え。primary 自体は変えない。 */
.theme--dark .customer-info-step .v-btn--outlined.primary--text {
  color: rgba(255, 255, 255, 0.92) !important;
  border-color: rgba(255, 255, 255, 0.55) !important;
  caret-color: rgba(255, 255, 255, 0.92) !important;
}
.theme--dark .customer-info-step .v-btn--outlined.primary--text .v-icon {
  color: rgba(255, 255, 255, 0.92) !important;
}
/* Step4 「個別に設定する」テキストボタンに常時の枠を付けて押下可能感を強調 */
.set-confirm-step .wizard-section-header .v-btn--text.primary--text {
  border: 1px solid currentColor;
}
.theme--light .set-confirm-step .wizard-section-header .v-btn--text.primary--text {
  color: rgba(0, 0, 0, 0.78) !important;
  border-color: rgba(0, 0, 0, 0.3) !important;
}
.theme--dark .set-confirm-step .wizard-section-header .v-btn--text.primary--text {
  color: rgba(255, 255, 255, 0.92) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Step4 サマリーカード内の primary 紫 (時刻・合計金額) はダークで背景にかぶるため白に */
.theme--dark .set-confirm-step .confirm-summary .primary--text {
  color: #fff !important;
}

/* 指名/リファラルタブ: ボタン風で押せるとわかるデザイン */
.nomination-tabs .v-tabs-bar {
  background: transparent !important;
}
.nomination-tabs .v-tab {
  min-width: 110px;
  border-radius: 8px;
  margin-right: 8px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: none;
  border: 1px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  position: relative;
}

/* ライトモード: 非アクティブはアウトラインで「押せる」感を出す。
   アクティブは primary 塗りつぶしのボタン風で、文字は白に。 */
.theme--light .nomination-tabs .v-tab {
  color: rgba(0, 0, 0, 0.72) !important;
  border-color: rgba(0, 0, 0, 0.18);
}
.theme--light .nomination-tabs .v-tab.v-tab--active {
  color: #fff !important;
  background-color: var(--v-primary-base) !important;
  border-color: var(--v-primary-base);
  font-weight: 700;
}
.theme--light .nomination-tabs .v-tab.v-tab--active .v-btn__content,
.theme--light .nomination-tabs .v-tab.v-tab--active * {
  color: #fff !important;
}
.theme--light .nomination-tabs .v-tab:hover:not(.v-tab--active) {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.25);
}

/* ダークモード: 紫が背景に潰れるため白系で代替（テーマ追従） */
.theme--dark .nomination-tabs .v-tab {
  color: rgba(255, 255, 255, 0.78) !important;
  border-color: rgba(255, 255, 255, 0.22);
}
.theme--dark .nomination-tabs .v-tab.v-tab--active {
  color: #fff !important;
  background-color: rgba(255, 255, 255, 0.14) !important;
  border-color: rgba(255, 255, 255, 0.5);
  font-weight: 700;
}
.theme--dark .nomination-tabs .v-tab:hover:not(.v-tab--active) {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.35);
}

/* Vuetify の primary--text を完全に無効化 */
.nomination-tabs .v-tab.primary--text,
.nomination-tabs .v-tab.primary--text *,
.nomination-tabs .v-tab .v-btn__content {
  color: inherit !important;
}
</style>
